import React, { useEffect, useState } from 'react';
import { useAuth } from '../../src/context/AuthContext';
import AdminSearchTable from '../../Components/Admin/AdminSearchTable';
import EmptyHeader from '../../Components/Shared/EmptyHeader';
import AddPolicyModal from '../../Components/PolicyHolder/AddPolicyModal';
import AddEntityPolicyModal from '../../Components/PolicyHolder/AddEntityPolicyModal';
import { useNavigate } from 'react-router-dom';

const LinkedPoliciesPage = ({ portalSettings }) => {
    // Auth
    const { user, loading: authLoading, logout } = useAuth();
    const navigate = useNavigate();

    // State Variables
    const [loading, setLoading] = useState(true);
    const [policies, setPolicies] = useState([]);
    const [columns, setColumns] = useState([]);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [showAddEntityModal, setShowAddEntityModal] = useState(false);

    // ENV Variables
    const companyName = import.meta.env.VITE_COMPANY_NAME;
    const apiURL = import.meta.env.VITE_API;

    // Set Site Title
    document.title = `${companyName} - Linked Policies`;

    // Fetch linked policies
    const fetchLinkedPolicies = async () => {
        if (!user?.userID) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const res = await fetch(
                `${apiURL}/account/GetLinkedPolicies?userID=${user.userID}`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );

            if (res.status === 401) {
                logout();
                navigate('/login');
                return;
            }

            if (res.status === 403) {
                navigate('/home');
                return;
            }

            if (!res.ok) {
                console.error(
                    'Failed to fetch linked policies:',
                    res.status,
                    res.statusText
                );

                setPolicies([]);
                return;
            }

            const data = await res.json();

            const tableColumns = [
                {
                    label: 'Policy Number',
                    accessor: 'polNo',
                },
                {
                    label: 'Policy Holder Name',
                    accessor: 'policyHolderName',
                },
                {
                    label: 'Status',
                    accessor: 'status',
                },
                {
                    label: 'Status Description',
                    accessor: 'statusDescription',
                },
            ];

            setColumns(tableColumns);
            setPolicies(data);
        } catch (err) {
            console.error('Error fetching linked policies:', err);
            setPolicies([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading) {
            fetchLinkedPolicies();
        }
    }, [user?.userID, authLoading]);

    const handleAddUserPolicy = async (formData) => {
        try {
            const payload = {
                polNo: formData.polNo,
                firstName: formData.firstName,
                lastName: formData.lastName,
                birthDate: formData.birthDate,
                lastFour: formData.last4SSN,
            };

            const res = await fetch(`${apiURL}/account/AddUserPolicy`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                return;
            }

            if (res.status === 403) {
                navigate('/home');
                return;
            }

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));

                alert(err.message || 'Failed to add policy.');
                return;
            }

            await fetchLinkedPolicies();

            setShowAddUserModal(false);
        } catch (error) {
            console.error('Error adding policy:', error);
            alert('An error occurred while adding the policy.');
        }
    };

    const handleAddEntityPolicy = async (formData) => {
        try {
            const payload = {
                polNo: formData.polNo,
                entityName: formData.entityName,
                state: formData.state,
                zip: formData.zip,
                fullTIN: formData.fullTIN,
            };

            const res = await fetch(`${apiURL}/account/AddEntityPolicy`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (res.status === 401) {
                logout();
                navigate('/login');
                return;
            }

            if (res.status === 403) {
                navigate('/home');
                return;
            }

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));

                alert(err.message || 'Failed to add policy.');
                return;
            }

            await fetchLinkedPolicies();

            setShowAddEntityModal(false);
        } catch (error) {
            console.error('Error adding policy:', error);
            alert('An error occurred while adding the policy.');
        }
    };

    if (authLoading || loading) {
        return <></>;
    }

    return (
        <>
            <div className="container container-section">
                <EmptyHeader />

                {!policies.length ? (
                    <div className="linked-policies-empty">
                        <div className="linked-policies-empty-content">
                            <h2 className="linked-policies-empty-title">
                                No Linked Policies
                            </h2>

                            <p className="linked-policies-empty-message">
                                No policies have been linked to your account yet.
                                To get started, select the type of policy you
                                would like to add below.
                            </p>

                            <div className="linked-policies-options">
                                <div className="linked-policies-option">
                                    <h4>Entity-Owned Policy</h4>

                                    <p>
                                        For policies owned by a business, trust,
                                        corporation, or other entity.
                                    </p>

                                    <button
                                        type="button"
                                        className="baseButton"
                                        onClick={() =>
                                            setShowAddEntityModal(true)
                                        }
                                    >
                                        ➕ Add Entity Policy
                                    </button>
                                </div>

                                <div className="linked-policies-option">
                                    <h4>Individually-Owned Policy</h4>

                                    <p>
                                        For policies owned by an individual
                                        person.
                                    </p>

                                    <button
                                        type="button"
                                        className="baseButton"
                                        onClick={() =>
                                            setShowAddUserModal(true)
                                        }
                                    >
                                        ➕ Add Individual Policy
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="linked-policies-actions">
                            <button
                                type="button"
                                className="baseButton"
                                onClick={() => setShowAddUserModal(true)}
                            >
                                ➕ Add Individual Policy
                            </button>

                            <button
                                type="button"
                                className="baseButton"
                                onClick={() => setShowAddEntityModal(true)}
                            >
                                ➕ Add Entity Policy
                            </button>
                        </div>

                        <AdminSearchTable
                            columns={columns}
                            data={policies}
                        />
                    </>
                )}
            </div>

            <AddPolicyModal
                isOpen={showAddUserModal}
                onClose={() => setShowAddUserModal(false)}
                onSubmit={handleAddUserPolicy}
                portalSettings={portalSettings}
            />

            <AddEntityPolicyModal
                isOpen={showAddEntityModal}
                onClose={() => setShowAddEntityModal(false)}
                onSubmit={handleAddEntityPolicy}
                portalSettings={portalSettings}
            />
        </>
    );
};

export default LinkedPoliciesPage;